<template>
  <v-card flat>
    <v-tabs v-model="tab" show-arrows background-color="blue lighten-5">
      <v-tab>Général</v-tab><v-tab>Enrichissements</v-tab><v-tab>{{ isTableMode ? 'Colonnes du tableau' : 'Visuels' }}</v-tab><v-tab>Design</v-tab><v-tab>Actions</v-tab>
    </v-tabs>
    <v-tabs-items v-model="tab" class="pa-4">
      <v-tab-item>
        <v-select v-model="displays.params.display.mode" :items="displayModes" label="Mode d’affichage" outlined dense />
        <v-alert type="info" text dense>
          Le fournisseur métier calcule les événements et déclare son contrat. Les requêtes complémentaires enrichissent ensuite uniquement les événements chargés.
        </v-alert>
        <v-autocomplete v-model="displays.params.provider.name" :items="providers" label="Fournisseur CustomDatasMetier" clearable outlined dense :loading="loadingProviders" @change="loadProviderSchema" />
        <v-alert v-if="!module.id" type="warning" text dense>Enregistrez d’abord le module pour découvrir les fournisseurs disponibles.</v-alert>
        <template v-if="providerSchema">
          <v-alert color="blue" border="left" text dense>{{ providerSchema.description }}</v-alert>
          <v-row v-for="parameter in providerSchema.parameters || []" :key="parameter.key" dense>
            <v-col cols="12" md="4"><strong>{{ parameter.label || parameter.key }}</strong><div class="text-caption">{{ parameter.type }}<span v-if="parameter.required"> · obligatoire</span></div></v-col>
            <v-col cols="12" md="8">
              <v-select v-if="parameter.options" :value="providerParameter(parameter.key)" :items="parameter.options" :label="`Valeur de ${parameter.key}`" clearable outlined dense @input="setProviderParameter(parameter.key, $event)" />
              <v-text-field v-else :value="providerParameter(parameter.key)" :label="`Valeur de ${parameter.key}`" placeholder="[[params||id]]" outlined dense @input="setProviderParameter(parameter.key, $event)" />
            </v-col>
          </v-row>
        </template>
        <v-divider class="my-4" />
        <v-row dense>
          <v-col cols="12" md="4"><v-text-field v-model.number="displays.params.pagination.limit" type="number" min="1" max="500" label="Événements par chargement" outlined dense /></v-col>
          <v-col cols="12" md="4"><v-select v-model="displays.params.pagination.order" :items="orders" label="Ordre chronologique" outlined dense /></v-col>
          <v-col cols="12" md="4"><v-text-field v-model="displays.params.pagination.loadMoreLabel" label="Texte charger plus" outlined dense /></v-col>
        </v-row>
      </v-tab-item>

      <v-tab-item>
        <v-alert type="info" text dense>Chaque requête reçoit automatiquement les IDs des événements visibles. Elle ne charge pas toutes les lignes de sa table.</v-alert>
        <div class="text-right mb-3"><v-btn small color="primary" @click="addEnrichment"><v-icon left small>mdi-plus</v-icon>Ajouter une requête</v-btn></div>
        <v-expansion-panels v-model="enrichmentPanel" accordion>
          <v-expansion-panel v-for="(query, index) in displays.params.enrichments" :key="index">
            <v-expansion-panel-header class="py-2">
              <div class="d-flex align-center">
                <v-icon small class="mr-2">mdi-database-search</v-icon>
                <span class="font-weight-medium">{{ query.name || `Enrichissement ${index + 1}` }}</span>
                <v-chip x-small class="ml-2" :color="query.enabled ? 'success' : 'grey'" dark>
                  {{ query.enabled ? 'Actif' : 'Inactif' }}
                </v-chip>
              </div>
              <template #actions>
                <v-icon>mdi-chevron-down</v-icon>
              </template>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <TimelineQueryEditor
                :config="query"
                :tables="tables"
                :provider-fields="providerFieldOptions"
                :event-types="eventTypeOptions"
                :page="page"
                :modal-key="modalKey"
                @change="value => $set(displays.params.enrichments, index, value)"
                @remove="removeEnrichment(index)"
              />
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-tab-item>

      <v-tab-item>
        <template v-if="isTableMode">
          <v-alert type="info" text dense>
            Ajoutez et réordonnez les colonnes avec constructVisuel. Chaque événement devient une ligne du tableau.
            Les colonnes et les visuels de timeline sont conservés séparément. L’ordre des lignes reste chronologique ; le tri des colonnes n’est pas appliqué.
          </v-alert>
          <Visuels :visuels="displays.params.display.columns" :fields="visualFields" :page="page" :modalKey="modalKey" @recepVal="value => $set(displays.params.display, 'columns', value)" />
        </template>
        <template v-else>
        <v-alert type="info" text dense>
          Les quatre zones utilisent constructVisuel. Le rond central peut afficher une quantité, un stock, une icône ou une image. Les variables sont de la forme <code>[[table||alias]]</code> ou <code>[[table||details alias]]</code>.
        </v-alert>
        <div class="d-flex align-center mb-3"><h3>Modèles visuels</h3><v-spacer /><v-btn small color="primary" @click="addVariant"><v-icon left small>mdi-plus</v-icon>Ajouter un modèle</v-btn></div>
        <v-expansion-panels accordion>
          <v-expansion-panel v-for="(variant, index) in displays.params.display.variants" :key="index">
            <v-expansion-panel-header>{{ variant.key || `Modèle ${index + 1}` }}</v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row dense>
                <v-col cols="12" md="4"><v-text-field v-model="variant.key" label="Clé du modèle" outlined dense /></v-col>
                <v-col cols="12" md="4"><v-text-field v-model="variant.appearance.bubbleColor" label="Couleur du rond" clearable outlined dense /></v-col>
                <v-col cols="12" md="4"><v-text-field v-model="variant.appearance.cardColor" label="Couleur de la carte" clearable outlined dense /></v-col>
              </v-row>
              <groupConditions v-if="variant.key !== 'default'" :conditions="variant.conditions" @recepVal="value => variant.conditions = value" />
              <v-tabs v-model="variantTabs[index]" grow background-color="grey lighten-4" class="mt-3"><v-tab>Gauche</v-tab><v-tab>Droite</v-tab><v-tab>Rond central</v-tab><v-tab>Tooltip</v-tab></v-tabs>
              <v-tabs-items v-model="variantTabs[index]">
                <v-tab-item v-for="slot in visualSlots" :key="slot.value" class="pt-3">
                  <Visuels :visuels="variant[slot.value]" :fields="visualFields" :page="page" :modalKey="modalKey" @recepVal="value => $set(variant, slot.value, value)" />
                </v-tab-item>
              </v-tabs-items>
              <div v-if="variant.key !== 'default'" class="text-right mt-3"><v-btn small text color="error" @click="removeVariant(index)"><v-icon left small>mdi-delete</v-icon>Supprimer</v-btn></div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
        </template>
      </v-tab-item>

      <v-tab-item>
        <TimelineConversionSettings :value="displays.params.display.conversion" :fields="conversionFields" :collision="conversionCollision" @input="value => $set(displays.params.display, 'conversion', value)" />
        <template v-if="!isTableMode">
        <v-row dense>
          <v-col cols="12" md="4"><v-select v-model="displays.params.display.layout.side" :items="sides" label="Disposition" outlined dense /></v-col>
          <v-col cols="12" md="4"><v-switch v-model="displays.params.display.layout.dense" label="Timeline compacte" color="primary" /></v-col>
          <v-col cols="12" md="4"><v-switch v-model="displays.params.display.layout.alignTop" label="Aligner en haut" color="primary" /></v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="3"><v-text-field v-model.number="displays.params.display.layout.bubbleSize" type="number" min="24" max="180" label="Diamètre du rond" suffix="px" outlined dense /></v-col>
          <v-col cols="12" md="3"><v-text-field v-model="displays.params.display.layout.bubbleColor" label="Couleur des ronds" clearable outlined dense /></v-col>
          <v-col cols="12" md="3"><v-text-field v-model="displays.params.display.layout.lineColor" label="Couleur de la ligne" clearable outlined dense /></v-col>
          <v-col cols="12" md="3"><v-text-field v-model="displays.params.display.layout.cardColor" label="Couleur des cartes" clearable outlined dense /></v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="4"><v-text-field v-model="displays.params.display.layout.lineClass" label="Classes CSS timeline" outlined dense /></v-col>
          <v-col cols="12" md="4"><v-text-field v-model="displays.params.display.layout.itemClass" label="Classes CSS événements" outlined dense /></v-col>
          <v-col cols="12" md="4"><v-text-field v-model="displays.params.display.layout.cardClass" label="Classes CSS cartes" outlined dense /></v-col>
        </v-row>
        <v-switch v-model="displays.params.display.tooltip.enabled" label="Activer le tooltip du rond" color="primary" />
        <v-row v-if="displays.params.display.tooltip.enabled" dense>
          <v-col cols="12" md="6"><v-select v-model="displays.params.display.tooltip.position" :items="tooltipPositions" label="Position du tooltip" outlined dense /></v-col>
          <v-col cols="12" md="6"><v-text-field v-model.number="displays.params.display.tooltip.minWidth" type="number" label="Largeur minimale" suffix="px" outlined dense /></v-col>
        </v-row>
        </template>
        <v-text-field v-model="displays.params.display.emptyText" label="Texte si aucun événement" outlined dense />
      </v-tab-item>

      <v-tab-item>
        <v-alert type="info" text dense>Créez les actions du module puis sélectionnez celles affichées sur chaque événement, en timeline ou en tableau. Leurs conditions utilisent les alias complets de l’événement.</v-alert>
        <v-autocomplete v-model="displays.params.actions" :items="actionOptions" label="Actions affiliées aux événements" multiple chips deletable-chips clearable outlined dense />
        <listActions :page="page" :module="module" :modalKey="modalKey" @listActions="setActions" />
      </v-tab-item>
    </v-tabs-items>
  </v-card>
</template>

<script>
import { admin } from '../mixins'
import TimelineQueryEditor from './queryEditor.vue'
import TimelineConversionSettings from './ConversionSettings.vue'

export default {
  name: 'TimelineAdmin',
  mixins: [admin],
  components: {
    TimelineQueryEditor,
    TimelineConversionSettings,
    Visuels: () => import('../../engine/outils/constructVisuel/Visuel.vue'),
  },
  data() {
    return {
      tab: 0, displays: this.module.displays, providers: [], providerSchema: null,
      loadingProviders: false, tables: [], actionOptions: [], variantTabs: {}, enrichmentPanel: undefined,
      orders: [{ text: 'Plus récent en premier', value: 'desc' }, { text: 'Plus ancien en premier', value: 'asc' }],
      displayModes: [{ text: 'Timeline', value: 'timeline' }, { text: 'Tableau', value: 'table' }],
      sides: [{ text: 'Alternée', value: 'alternate' }, { text: 'À droite', value: 'right' }, { text: 'À gauche', value: 'left' }],
      tooltipPositions: [{ text: 'Droite', value: 'right' }, { text: 'Gauche', value: 'left' }, { text: 'Haut', value: 'top' }, { text: 'Bas', value: 'bottom' }],
      visualSlots: [{ text: 'Gauche', value: 'left' }, { text: 'Droite', value: 'right' }, { text: 'Rond', value: 'middle' }, { text: 'Tooltip', value: 'tooltip' }],
    }
  },
  computed: {
    isTableMode() { return this.displays.params.display.mode === 'table' },
    providerFieldOptions() {
      return (this.providerSchema && this.providerSchema.fields ? this.providerSchema.fields : []).map(field => ({ text: `${field.label || field.key} (${field.key})`, value: field.key }))
    },
    eventTypeOptions() {
      const values = (this.providerSchema && this.providerSchema.event_types) || []
      return values.map(item => typeof item === 'string' ? item : item.value)
    },
    rawVisualFields() {
      const fields = [...this.providerFieldOptions]
      const enrichments = this.displays.params.enrichments || []
      enrichments.forEach(query => {
        const queryFields = query.champs || []
        queryFields.filter(field => field && field.as).forEach(field => {
          const value = query.target ? `${query.target} ${field.as}` : field.as
          fields.push({ text: `${query.name || query.target || 'Requête'} : ${field.as}`, value })
        })
      })
      return fields
    },
    conversionFields() {
      const numericTypes = /^(number|numeric|integer|int|float|double|decimal|bigint|smallint|tinyint)$/i
      const fields = ((this.providerSchema && this.providerSchema.fields) || [])
        .filter(field => numericTypes.test(field.type || ''))
        .map(field => ({ text: `${field.label || field.key} (${field.key})`, value: field.key }))
      ;(this.displays.params.enrichments || []).filter(query => query.enabled !== false && query.cardinality !== 'many').forEach(query => {
        (query.champs || []).filter(field => field && field.as && field.as !== query.correlationAlias).forEach(field => {
          const value = query.target ? `${query.target}.${field.as}` : field.as
          fields.push({ text: `${query.name || 'Requête'} : ${value} (valeur numérique)`, value })
        })
      })
      const keys = new Set(this.rawVisualFields.map(field => field.value.replace(/\s+/g, '.')))
      return fields.filter(field => !keys.has(`${field.value}_display`) && !keys.has('display_unit'))
    },
    conversionCollision() {
      const keys = this.rawVisualFields.map(field => field.value.replace(/\s+/g, '.'))
      return keys.includes('display_unit') || keys.some(key => key.endsWith('_display') && keys.includes(key.slice(0, -8)))
    },
    visualFields() {
      const fields = [...this.rawVisualFields]
      const conversion = this.displays.params.display.conversion
      if (!conversion || conversion.enabled !== true) return fields
      const selected = new Set(conversion.fields || [])
      this.conversionFields.filter(field => selected.has(field.value)).forEach(field => {
        fields.push({ text: `${field.text} — converti`, value: `${field.value.replace(/\./g, ' ')}_display` })
      })
      if (!fields.some(field => field.value === 'display_unit')) fields.push({ text: 'Unité affichée (display_unit)', value: 'display_unit' })
      return fields
    },
  },
  created() {
    this.ensureDefaults()
  },
  mounted() {
    this.API({ type: 'get', url: 'engine/getlisttable' }).then(response => {
      this.tables = (response.data || []).map(table => ({ text: table, value: table }))
    })
    this.loadProviders()
  },
  methods: {
    ensureDefaults() {
      const params = this.displays.params
      if (!params.provider) this.$set(params, 'provider', { name: '', parameters: [] })
      if (!Array.isArray(params.provider.parameters)) this.$set(params.provider, 'parameters', [])
      if (!Array.isArray(params.enrichments)) this.$set(params, 'enrichments', [])
      if (!Array.isArray(params.actions)) this.$set(params, 'actions', [])
      if (!params.display.mode) this.$set(params.display, 'mode', 'timeline')
      if (!Array.isArray(params.display.columns)) this.$set(params.display, 'columns', [])
      if (!params.display.conversion) this.$set(params.display, 'conversion', { enabled: false, family: 'volume', storageUnit: 'ml', displayUnit: 'hl', decimals: 2, fields: [] })
      if (!params.display.variants || !params.display.variants.length) this.$set(params.display, 'variants', [this.newVariant('default')])
    },
    callModule(operation, extra = {}) {
      if (!this.module.id) return Promise.resolve(null)
      return this.API({ type: 'post', url: 'displaymodule', params: { id: this.module.id, operation, ...extra } }).then(response => response.data || response)
    },
    loadProviders() {
      if (!this.module.id) return
      this.loadingProviders = true
      this.callModule('providers').then(data => {
        this.providers = (data && data.providers) || []
        if (this.displays.params.provider.name) this.loadProviderSchema()
      }).finally(() => { this.loadingProviders = false })
    },
    loadProviderSchema() {
      this.providerSchema = null
      if (!this.displays.params.provider.name) return
      this.callModule('describeProvider', { provider: this.displays.params.provider.name }).then(data => {
        this.providerSchema = data && data.schema ? data.schema : null
        ;(this.providerSchema && this.providerSchema.parameters ? this.providerSchema.parameters : []).forEach(parameter => {
          if (this.providerParameter(parameter.key) === undefined) this.setProviderParameter(parameter.key, parameter.default || '')
        })
      })
    },
    providerParameter(key) {
      const found = this.displays.params.provider.parameters.find(item => item.key === key)
      return found ? found.value : undefined
    },
    setProviderParameter(key, value) {
      const index = this.displays.params.provider.parameters.findIndex(item => item.key === key)
      const item = { key, value }
      if (index < 0) this.displays.params.provider.parameters.push(item)
      else this.$set(this.displays.params.provider.parameters, index, item)
    },
    addEnrichment() {
      this.displays.params.enrichments.push({ enabled: true, name: 'Nouvelle requête', table: '', eventTypes: [], collectField: 'source_id', queryFilterField: '', correlationAlias: '', valueType: 'integer', cardinality: 'one', target: '', champs: [], filtre: [] })
      this.enrichmentPanel = this.displays.params.enrichments.length - 1
    },
    removeEnrichment(index) {
      this.displays.params.enrichments.splice(index, 1)
      this.enrichmentPanel = undefined
    },
    newVariant(key = '') {
      return { key, conditions: [], appearance: { bubbleColor: '', cardColor: '', icon: '' }, left: [], right: [], middle: [], tooltip: [] }
    },
    addVariant() { this.displays.params.display.variants.push(this.newVariant(`modele-${this.displays.params.display.variants.length + 1}`)) },
    removeVariant(index) { this.displays.params.display.variants.splice(index, 1) },
    setActions(actions) { this.actionOptions = (actions || []).map(action => ({ text: action.name, value: action.id })) },
  },
}
</script>
